import { Extension } from "@tiptap/core";
import { Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { FormulaDataCustomValues } from "../../ExpressionEditor.types";
import { getTransformedParagraphs } from "./getTransformedParagraphs";

interface PasteTransformInternalConfig {
  allowedVariables: Array<string>;
  allowedCustomValues: Array<string>;
  currentCustomValues: FormulaDataCustomValues;
}

interface PasteTransformOptions {
  allowedVariables: Array<string>;
}

interface PasteTransformStorage {
  allowedCustomValues: Array<string>;
}

declare module "@tiptap/react" {
  interface Storage {
    pasteTransform: PasteTransformStorage;
  }
}

/**
 * Tiptap extension to process text content being pasted into the editor,
 * transforming recognized variables and/or template custom values into actual editor nodes.
 */
const PasteTransform = Extension.create<
  PasteTransformOptions,
  PasteTransformStorage
>({
  name: "pasteTransform",
  addOptions() {
    return {
      allowedVariables: []
    };
  },
  addStorage() {
    return {
      allowedCustomValues: []
    };
  },
  addProseMirrorPlugins() {
    const allowedVariables = this.options.allowedVariables;

    const getAllowedCustomValues = () =>
      this.editor.storage.pasteTransform.allowedCustomValues || [];
    const getCurrentCustomValues = () =>
      this.editor.storage.customValueInTemplate.values;

    return [
      new Plugin({
        key: new PluginKey("pasteTransform"),
        props: {
          transformPasted(slice) {
            const allowedCustomValues = getAllowedCustomValues();
            const currentCustomValues = getCurrentCustomValues();

            const transformedParagraphs = getTransformedParagraphs({
              fragment: slice.content,
              config: {
                allowedVariables,
                allowedCustomValues,
                currentCustomValues
              }
            });

            // new slice openStart & openEnd set to 0 to indicate that transformedParagraphs is a complete document fragment
            const newSlice = new Slice(transformedParagraphs, 0, 0);

            return newSlice;
          }
        }
      })
    ];
  }
});

export { PasteTransform, PasteTransformInternalConfig };
